// Copyright 2025 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from "@reduxjs/toolkit";

/**
 * ================================
 * WebSocket Global State Slice
 * ================================
 *
 * This slice ONLY manages the WebSocket connection state to server:
 * - connection status (connecting / connected / disconnected / error)
 * - last error message (if any)
 *
 * IMPORTANT:
 * - This slice does NOT store WebSocket messages(wsEventHandlers handle messages)
 * - UI components can safely rely on this slice to reflect connection health to server
 */

const initialState = {
  status: "disconnected",
  // Possible values:
  // 'connecting'  -> connection attempt in progress
  // 'connected'   -> WebSocket is open and ready
  // 'disconnected'-> closed intentionally or not connected yet
  // 'error'       -> unrecoverable error occurred

  error: null, // string | null — last connection or message error
};

const webSocketSlice = createSlice({
  name: "webSocket",
  initialState,
  reducers: {
    /**
     * Fired when a WebSocket connection attempt starts
     */
    webSocketConnecting: (state) => {
      state.status = "connecting";
      state.error = null;
    },

    /**
     * Fired when the WebSocket connection is successfully opened
     */
    webSocketConnected: (state) => {
      state.status = "connected";
      state.error = null;
    },

    /**
     * Fired when the WebSocket is closed
     * (either manually or due to network/server issues)
     */
    webSocketClosed: (state) => {
      state.status = "disconnected";
    },

    /**
     * Fired when a connection-level error occurs
     * (e.g. connection refused, too many reconnects)
     */
    webSocketError: (state, action) => {
      state.status = "error";
      state.error = action.payload;
    },

    /**
     * Fired when a message is received but cannot be processed
     * (e.g. invalid JSON, handler error)
     */
    webSocketMessageError: (state, action) => {
      state.error =
        action.payload || "Unable to process incoming WebSocket message";
    },

    /**
     * Clears the last WebSocket error message
     */
    clearWebSocketError: (state) => {
      state.error = null;
    },
  },
});

export const {
  webSocketConnecting,
  webSocketConnected,
  webSocketClosed,
  webSocketError,
  webSocketMessageError,
  clearWebSocketError,
} = webSocketSlice.actions;

export default webSocketSlice.reducer;
